/**
 * Allegati di un'offerta, aperti direttamente dalla riga dell'elenco.
 *
 * Caricando un documento che prova l'accettazione (ordine di acquisto,
 * offerta firmata, contratto) l'offerta si può segnare come vinta. Prima però
 * si chiede conferma sull'importo: fra l'offerta inviata e l'ordine ricevuto
 * il cliente spesso lima qualcosa, e quel valore deve arrivare corretto in
 * commessa — è il budget su cui si misureranno margine e fatturazione.
 */
import { useState } from 'react';
import Modal from './Modal';
import PannelloAllegati, { CATEGORIE_ACCETTAZIONE } from './PannelloAllegati';
import { offerteApi } from '../api';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

export default function ModaleAllegati({ offerta, onClose, onAggiornata, onCreaCommessa }) {
  // Il blocco di accettazione compare solo se l'offerta non è già decisa
  const decisa = ['vinta', 'persa'].includes(offerta.stato);
  const [proponi, setProponi] = useState(null);      // allegato che ha innescato la proposta
  const [importo, setImporto] = useState(String(offerta.importo ?? ''));
  const [salvando, setSalvando] = useState(false);

  const suCaricato = (a) => {
    if (decisa) return;
    if (!CATEGORIE_ACCETTAZIONE.includes(a.categoria)) return;
    setImporto(String(offerta.importo ?? ''));
    setProponi(a);
  };

  const variato = Number(importo) !== Number(offerta.importo);

  const conferma = async (ancheCommessa) => {
    setSalvando(true);
    try {
      const aggiornata = await offerteApi.update(offerta.id, {
        ...offerta,
        stato: 'vinta',
        importo: Number(importo) || 0,
      });
      onAggiornata?.();
      if (ancheCommessa) onCreaCommessa?.({ ...offerta, ...aggiornata });
      else { setProponi(null); onClose(); }
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  return (
    <Modal title={`Allegati — ${offerta.numero}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          <strong className="text-gray-900">{offerta.titolo}</strong>
          {offerta.cliente_nome && <> · {offerta.cliente_nome}</>}
          <> · {fmt(offerta.importo)}</>
        </div>

        <PannelloAllegati
          tipo="offerta"
          id={offerta.id}
          onCaricato={suCaricato}
          titolo="Documenti dell'offerta"
        />

        {/* Proposta di accettazione */}
        {proponi && (
          <div className="bg-green-50 border border-green-300 rounded-lg p-3 space-y-3">
            <div className="text-sm text-gray-800">
              Hai caricato un documento di accettazione
              (<strong>{proponi.nome_file}</strong>).
              Vuoi segnare l'offerta come <strong>vinta</strong>?
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-0.5">
                  Importo confermato dal cliente (€)
                </label>
                <input className="input text-sm w-44" type="number" step="0.01"
                       value={importo} onChange={e => setImporto(e.target.value)} />
              </div>
              {variato && (
                <div className="text-xs text-amber-700 pb-2">
                  Diverso dall'offerta ({fmt(offerta.importo)}): verrà aggiornato
                  anche il budget della commessa.
                </div>
              )}
              {!variato && (
                <div className="text-xs text-gray-500 pb-2">
                  Correggilo se l'ordine riporta un importo diverso.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1 border-t border-green-200">
              <button className="btn-secondary btn-sm" disabled={salvando}
                      onClick={() => setProponi(null)}>Non ora</button>
              <button className="btn-secondary btn-sm" disabled={salvando}
                      onClick={() => conferma(false)}>
                {salvando ? '…' : 'Solo segna come vinta'}
              </button>
              <button className="btn-primary btn-sm" disabled={salvando}
                      onClick={() => conferma(true)}>
                {salvando ? '…' : '✓ Vinta e crea commessa'}
              </button>
            </div>
          </div>
        )}

        {decisa && (
          <p className="text-xs text-gray-400">
            Offerta già {offerta.stato}: qui puoi solo aggiungere o togliere documenti.
          </p>
        )}

        <div className="flex justify-end pt-2 border-t">
          <button className="btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </Modal>
  );
}
